/**
 * Money arithmetic in integer minor units. Splits use the largest-remainder method so the parts
 * always add back to the source amount exactly, and the same input always rounds the same way.
 */
import type { Allocation, AllocationDimension, AllocationKind } from "./types";

export const DIMENSION: Record<AllocationKind, AllocationDimension> = {
  enterprise: "enterprise", group: "cattle", lot: "cattle", animal: "cattle", owner: "owner",
};

/** Split an amount by weights. The parts add up to `minor` exactly; earlier parts win ties. */
export function splitAmount(minor: number, weights: number[]): number[] {
  if (!weights.length) return [];
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return weights.map(() => 0);
  const sign = minor < 0 ? -1 : 1;
  const abs = Math.abs(minor);
  const raw = weights.map((w) => (abs * w) / total);
  const floors = raw.map((r) => Math.floor(r + 1e-9));
  let left = abs - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, rem: r - floors[i] })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const o of order) {
    if (left <= 0) break;
    floors[o.i] += 1;
    left -= 1;
  }
  return floors.map((f) => f * sign);
}

/**
 * Scale signed line amounts so they add up to `target` (the part of a document that was paid),
 * keeping each line's proportion and rounding so the parts sum exactly.
 */
export function prorate(amounts: number[], target: number): number[] {
  const total = amounts.reduce((a, b) => a + b, 0);
  if (!amounts.length || total === 0) return amounts.map(() => 0);
  const raw = amounts.map((a) => (a * target) / total);
  const floors = raw.map((r) => Math.floor(r + 1e-9));
  let left = target - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, rem: r - floors[i] })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const o of order) {
    if (left <= 0) break;
    floors[o.i] += 1;
    left -= 1;
  }
  return floors;
}

export interface AllocationIssue { path: string; message: string; values?: Record<string, string | number> }

/** Each dimension that is used must add up to exactly 100%. Ids may not repeat within a dimension. */
export function checkAllocations(allocations: Allocation[], path = "allocations"): AllocationIssue[] {
  const issues: AllocationIssue[] = [];
  const byDim = new Map<AllocationDimension, Allocation[]>();
  allocations.forEach((a, i) => {
    if (!(a.kind in DIMENSION)) { issues.push({ path: `${path}.${i}.kind`, message: "Unknown kind of share" }); return; }
    if (!a.id) issues.push({ path: `${path}.${i}.id`, message: "Pick who or what gets this share" });
    if (!Number.isFinite(a.share) || a.share <= 0 || a.share > 100) issues.push({ path: `${path}.${i}.share`, message: "A share must be more than 0% and at most 100%" });
    const dim = DIMENSION[a.kind];
    byDim.set(dim, [...(byDim.get(dim) ?? []), a]);
  });
  for (const [dim, list] of byDim) {
    const sum = Math.round(list.reduce((n, a) => n + (Number.isFinite(a.share) ? a.share : 0), 0) * 1000) / 1000;
    if (Math.abs(sum - 100) > 0.001) issues.push({ path: `${path}.${dim}`, message: "Shares add up to {{sum}}%. They need to add up to 100%.", values: { sum: Math.round(sum * 100) / 100 } });
    const seen = new Set<string>();
    for (const a of list) {
      const key = `${a.kind === "lot" ? "group" : a.kind}:${a.id}`;
      if (seen.has(key)) issues.push({ path: `${path}.${dim}`, message: "The same one is listed twice" });
      seen.add(key);
    }
  }
  return issues;
}

/** Split a line amount across one dimension's shares, exactly. */
export function allocate(minor: number, allocations: Allocation[], dimension: AllocationDimension): Array<{ allocation: Allocation; minor: number }> {
  const list = allocations.filter((a) => DIMENSION[a.kind] === dimension);
  const parts = splitAmount(minor, list.map((a) => a.share));
  return list.map((allocation, i) => ({ allocation, minor: parts[i] }));
}

/** Even shares that add to 100 with two decimals, e.g. 33.34, 33.33, 33.33. */
export function evenShares(n: number): number[] {
  if (n <= 0) return [];
  return splitAmount(10_000, Array(n).fill(1)).map((x) => x / 100);
}

export const sumMinor = (values: number[]): number => values.reduce((a, b) => a + b, 0);

/** Parse a typed amount ("1,240.50", "$80") into minor units, or null. */
export function parseAmount(text: string): number | null {
  const cleaned = text.replace(/[\s$,]/g, "");
  if (!cleaned || !/^-?\d*(\.\d{0,2})?$/.test(cleaned) || cleaned === "-" || cleaned === ".") return null;
  return Math.round(Number(cleaned) * 100);
}

// ---------------------------------------------------------------- periods

export type PeriodKey = "this_month" | "last_month" | "this_year" | "last_year" | "last_12_months" | "ytd" | "all";

function pad(n: number) { return String(n).padStart(2, "0"); }
function lastDay(y: number, m: number) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

/** Fiscal year containing a date, as [from, to]. `startMonth` is 1 for a calendar year. */
export function fiscalYear(date: string, startMonth = 1, offset = 0): { from: string; to: string; year: number } {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  let start = m >= startMonth ? y : y - 1;
  start += offset;
  const from = `${start}-${pad(startMonth)}-01`;
  const endY = startMonth === 1 ? start : start + 1;
  const endM = startMonth === 1 ? 12 : startMonth - 1;
  return { from, to: `${endY}-${pad(endM)}-${pad(lastDay(endY, endM))}`, year: endY };
}

export function periodRange(key: PeriodKey, today: string, startMonth = 1): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  switch (key) {
    case "this_month": return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(lastDay(y, m))}` };
    case "last_month": {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { from: `${py}-${pad(pm)}-01`, to: `${py}-${pad(pm)}-${pad(lastDay(py, pm))}` };
    }
    case "this_year": { const f = fiscalYear(today, startMonth); return { from: f.from, to: f.to }; }
    case "ytd": return { from: fiscalYear(today, startMonth).from, to: today };
    case "last_year": { const f = fiscalYear(today, startMonth, -1); return { from: f.from, to: f.to }; }
    case "last_12_months": {
      const start = new Date(Date.UTC(y, m - 12, 1));
      return { from: start.toISOString().slice(0, 10), to: today };
    }
    case "all": return { from: "1900-01-01", to: "9999-12-31" };
  }
}

/** Months (YYYY-MM) from one date to another, inclusive. */
export function monthsBetween(from: string, to: string, max = 36): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  const end = to.slice(0, 7);
  while (out.length < max) {
    const key = `${y}-${pad(m)}`;
    out.push(key);
    if (key >= end) break;
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}
